import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Star, Award, Crown, ShieldCheck, Camera, Lock, ArrowRight } from "lucide-react";
import { PayPalCheckoutButton } from "./PayPalCheckoutButton";

/**
 * 정회원 혜택보기 모달
 * - 일반회원(무료)은 생략, 정회원을 가장 크게 강조
 * - 정회원 결제하기 버튼 → /pricing (실제 결제)
 * - 이후 프리미엄/VIP를 정상 순서로 표시
 * - 정회원은 서류인증·사진 필수, 회원정보 열람은 정회원 이상만 가능함을 명시
 */

const REGULAR = {
  name: "정회원",
  nameEn: "Regular Membership",
  price: "USD $350",
  priceDetail: "/ 연간",
  features: [
    "모든 회원 프로필 자유 열람",
    "쪽지 · 관심 보내기 무제한",
    "주 1회 글로벌 줌미팅 참여",
    "연애사관학교 전체 강의 무제한",
    "오프라인 모임 우선 신청 + 20% 할인",
    "정회원만을 위한 공식 카톡방 초대",
  ],
};

const OTHER_TIERS = [
  {
    name: "프리미엄",
    nameEn: "Premium",
    price: "USD $700",
    priceDetail: "/ 연간",
    Icon: Award,
    accent: "text-stone-700",
    features: [
      "정회원 혜택 전체 포함",
      "리즈와 30분 1:1 인터뷰",
      "3:3 줌데이트 1회 무료 참여",
      "1:1 매칭 3회 이상 진행",
    ],
  },
  {
    name: "VIP",
    nameEn: "VIP",
    price: "USD $1,400",
    priceDetail: "/ 연간",
    Icon: Crown,
    accent: "text-amber-600",
    features: [
      "프리미엄 혜택 전체 포함",
      "리즈와 1시간 1:1 인터뷰",
      "3:3 줌미팅 3회 참여",
      "1:1 고급 매칭 3회 이상 진행",
    ],
  },
];

export default function PremiumBenefitsModal({ open, onClose }) {
  const navigate = useNavigate();

  const goPay = () => {
    onClose?.();
    navigate("/pricing");
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-start justify-center p-3 md:p-8 bg-black/55 overflow-y-auto"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="premium-benefits-modal"
        >
          <motion.div
            className="relative w-full max-w-3xl bg-[#FAFAF8] rounded-3xl overflow-hidden shadow-2xl my-auto"
            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white border border-stone-200 flex items-center justify-center text-stone-600 transition-colors"
              data-testid="premium-benefits-close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* === 정회원 강조 영역 (옅은 그레이) === */}
            <div className="bg-gradient-to-br from-stone-100 to-stone-200 text-stone-800 px-6 md:px-10 pt-9 pb-8">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 fill-current text-amber-400" />
                <span className="text-[12px] font-bold tracking-widest uppercase text-stone-400">{REGULAR.nameEn}</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold leading-tight text-stone-900">
                정회원은 서류 인증을 통해<br />싱글 여부가 검증된 회원입니다
              </h2>
              <div className="mt-4 flex items-end gap-1">
                <span className="text-3xl md:text-4xl font-extrabold text-stone-900">{REGULAR.price}</span>
                <span className="text-stone-500 text-sm mb-1">{REGULAR.priceDetail}</span>
              </div>

              <ul className="mt-5 grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
                {REGULAR.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[14px] text-stone-700">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-white flex items-center justify-center shrink-0 border border-stone-200">
                      <Check className="w-3.5 h-3.5 text-stone-600" />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* 정회원 결제하기 — 클릭 시 PayPal 결제창으로 바로 이동 */}
              <div className="mt-7" data-testid="premium-pay-wrap">
                <PayPalCheckoutButton
                  planId="annual_regular"
                  amount={350}
                  requireRealName={true}
                  label="정회원 결제하기  →"
                  testIdPrefix="premium-pay"
                  className="w-full py-4 rounded-2xl bg-burgundy-600 text-white font-bold text-[16px] hover:bg-burgundy-700 transition-colors flex items-center justify-center gap-2 shadow-sm disabled:opacity-60"
                />
              </div>
            </div>

            {/* === 필수 안내 === */}
            <div className="px-6 md:px-10 py-5 bg-amber-50 border-b border-amber-100 space-y-2.5">
              <div className="flex items-start gap-2.5">
                <ShieldCheck className="w-5 h-5 text-burgundy-600 shrink-0 mt-0.5" />
                <p className="text-[13.5px] text-stone-700 leading-relaxed">
                  <span className="font-bold">정회원은 서류 인증과 프로필 사진 등록이 필수</span>입니다. (안전한 만남을 위한 본인 확인 절차)
                </p>
              </div>
              <div className="flex items-start gap-2.5">
                <Lock className="w-5 h-5 text-burgundy-600 shrink-0 mt-0.5" />
                <p className="text-[13.5px] text-stone-700 leading-relaxed">
                  <span className="font-bold">모든 회원 정보 열람은 정회원 이상만 가능</span>합니다. (사진 · 프로필 · 쪽지 등)
                </p>
              </div>
              <div className="flex items-start gap-2.5">
                <Camera className="w-5 h-5 text-burgundy-600 shrink-0 mt-0.5" />
                <p className="text-[13.5px] text-stone-700 leading-relaxed">
                  인증을 마친 회원만 서로의 프로필을 확인할 수 있어, 더 진솔한 인연을 만날 수 있습니다.
                </p>
              </div>
            </div>

            {/* === 그 외 멤버십 (정상 순서: 프리미엄 → VIP) === */}
            <div className="px-6 md:px-10 py-6">
              <h3 className="text-[13px] font-bold text-stone-500 uppercase tracking-wider mb-3">상위 멤버십도 있어요</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {OTHER_TIERS.map((t) => {
                  const TI = t.Icon;
                  return (
                    <div key={t.name} className="rounded-2xl border border-stone-200 bg-white p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <TI className={`w-4 h-4 ${t.accent}`} />
                        <span className="font-bold text-stone-800">{t.name}</span>
                      </div>
                      <div className="flex items-end gap-1 mb-3">
                        <span className="text-xl font-extrabold text-stone-900">{t.price}</span>
                        <span className="text-stone-400 text-xs mb-0.5">{t.priceDetail}</span>
                      </div>
                      <ul className="space-y-1.5">
                        {t.features.map((f) => (
                          <li key={f} className="flex items-start gap-1.5 text-[12.5px] text-stone-600">
                            <Check className="w-3.5 h-3.5 text-stone-400 shrink-0 mt-0.5" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={goPay}
                className="mt-5 w-full py-3.5 rounded-2xl bg-burgundy-600 hover:bg-burgundy-700 text-white font-semibold transition-colors"
                data-testid="premium-view-all-pricing"
              >
                전체 멤버십 · 결제 옵션 보기
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
